import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useDrag } from 'react-dnd';
import styled from 'styled-components';
import { FileContext } from '../../context/FileContext';
import CheckboxEditOverlayChip from '../PDFOverlayChips/Checkbox/Edit';
import ImageEditOverlayChip from '../PDFOverlayChips/Image/Edit';
import SignDateEditOverlayChip from '../PDFOverlayChips/SignDate/Edit';
import TextAreaEditOverlayChip from '../PDFOverlayChips/TextArea/Edit';
import TextBoxEditOverlayChip from '../PDFOverlayChips/TextBox/Edit';

import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { default as APP_CONFID, default as APP_CONFIG } from '../../config';
import CustomTipseen from '../CustomTipseen/CustomTipseen';
import { LineItemEditOverlayChip } from '../LineItems';
import DropdownEditOverlayChip from '../PDFOverlayChips/Dropdown/Edit';
import LinkEditOverlayChip from '../PDFOverlayChips/Link/Edit';
import PrefilledFieldsEditOverlayChip from '../PDFOverlayChips/PrefilledFields/Edit';
import PrefilledFieldsImageEditOverlayChip from '../PDFOverlayChips/PrefilledFields/ImageEdit';
import SignatureAndInitialsEditChip from '../PDFOverlayChips/SignatureAndInitials/Edit';
import OptionsBar from './OptionsBar';
import useChipWrapper from '../ChipWrapper';
import DatePickerField from '../PDFOverlayChips/DatePicker';
import {
  isFeatureAvailable,
  FEATURES,
} from '../../constants/featureBasedPricing';

const SIGNATURE_FIELDS = [
  'receiver-initials',
  'sender-initials',
  'receiver-signature',
  'sender-signature',
  APP_CONFID.form_fields.signatureField.id,
  APP_CONFID.form_fields.initialsField.id,
];

const ChipContainer = styled.div`
  width: fit-content;
  position: absolute;
  z-index: 1;
  top: ${({ y }) => y + 'px'};
  left: ${({ x }) => x + 'px'};
  font-size: 11px;
  transition: all 0.1s ease-out;
  font-weight: 400;
  pointer-events: auto;
  .signature {
    min-width: 180px;
    border: 4px solid rgba(0, 115, 234, 0.4);
    border-radius: 4px;
    background-color: #e6e9ef;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 16px;
    font-size: 14px;
    line-height: 22px;
    height: 33px;
    color: #0073ea;
    font-weight: 400;

    &.cursor-grab {
      cursor: grab;
    }
    &.cursor-grabbing {
      cursor: grabbing;
    }
    svg {
      box-sizing: content-box;
      padding: 10px;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .item-value-container {
    position: relative;
    .options {
      position: absolute;
      top: -2rem;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      border: 1px solid #c5c7d0;
      filter: drop-shadow(0px 4px 8px rgba(197, 199, 208, 0.25));
      border-radius: 4px;
      box-shadow: 0px 4px 8px rgba(197, 199, 208, 0.25);
      padding: 5px 6.25px;
      z-index: 5;
      background-color: white;
      svg {
        cursor: pointer;
        height: 16px;
        width: 16px;
      }
    }

    div {
      /* border: 1px solid transparent; */
      &.hover-active:hover {
        border: 1px solid #3a87fd;
      }
      &.hover-active {
        border: 1px solid transparent;
      }
      &.active {
        border: 1px solid #3a87fd;
      }
      .resizeable {
        min-width: 3rem;
        height: fit-content;
        width: fit-content;
        cursor: move;
        &.active {
          border: 1px solid #3a87fd;
        }
      }

      .resizeable#checkbox,
      .resizeable#sign-date {
        min-width: auto;
        height: fit-content;
        width: fit-content;
        cursor: move;

        &.active {
          border: 1px solid #3a87fd !important;
          border-radius: 5px;
        }
      }

      &.cursor-grab {
        cursor: grab;
      }

      &.cursor-grabbing {
        cursor: grabbing;
      }
    }
  }

  .no-wrap {
    white-space: nowrap;
  }
`;

const CustomButtonWrapper = styled.div`
  padding: 5px 8px;
  width: max-content;
  color: white;
  border-radius: 4px;
  background-color: ${({ buttonColor }) => buttonColor || '#0073ea'};
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
`;

const DraggableChips = ({
  x,
  y,
  item,
  onItemClick,
  isSigningPage,
  openSignatureDialog,
  mondayBoardValues,
  handleRemovePlaceholder,
  isV3 = false,
}) => {
  const {
    documentRef,
    addedFormFields,
    updateFormFields,
    currentSelectedItem,
    setIsItemDragging,
    pricingPlan,
    slug,
  } = useContext(FileContext);

  const { t } = useTranslation();

  const draggableItemRef = useRef(null);
  const optionsRef = useRef(null);

  const { wrapComponent } = useChipWrapper();

  const [isResizing, setIsResizing] = useState(false);

  const [{ isDragging }, drag, dragPreview] = useDrag({
    item: { ...item },
    type: 'Chips',
    canDrag: !isResizing,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleBlur = (event) => {
    const selectedItem = addedFormFields[currentSelectedItem.current];
    if (selectedItem) selectedItem.text = event.target.innerText;
    updateFormFields([...addedFormFields]);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    }
  };

  useEffect(() => {
    if (isDragging) {
      setIsItemDragging(true);
    }
  }, [isDragging]);

  const attachRef = (el) => {
    drag(el);
    draggableItemRef.current = el;
  };

  const handeItemClick = (event) => {
    if (
      draggableItemRef.current &&
      !draggableItemRef.current.contains(event.target) &&
      optionsRef.current &&
      !optionsRef.current.contains(event.target)
    ) {
      currentSelectedItem.current = null;
      addedFormFields.forEach((item) => (item.isActive = false));
      updateFormFields([...addedFormFields]);
    }
  };

  useEffect(() => {
    documentRef?.current?.addEventListener('click', handeItemClick);

    return () => {
      documentRef?.current?.removeEventListener('click', handeItemClick);
    };
  }, [documentRef, addedFormFields]);

  const refStyle = {
    opacity: isDragging ? 0 : 1,
    textAlign: item?.fontStyle?.alignment || 'left',
    fontWeight: item?.fontStyle?.bold ? 800 : 400,
    fontStyle: item?.fontStyle?.italics ? 'italic' : 'normal',
    color:
      `rgb(${item?.fontStyle?.color.r}, ${item?.fontStyle?.color.g}, ${item?.fontStyle?.color.b})` ||
      'rgb(0,0,0)',
  };

  const standardFieldsAndSignature = [
    'checkbox',
    'sign-date',
    'text-box',
    'sd-dropdown',
    'sd-image',
    'line-item',
    'receiver-initials',
    'sender-initials',
    'receiver-signature',
    'sender-signature',
    'sd-payment',
    APP_CONFID.form_fields.signatureField.id,
    APP_CONFID.form_fields.initialsField.id,
    APP_CONFID.form_fields.standard_fields.datePicker.id,
  ];

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <ChipContainer
      x={x}
      y={y}
      {...(isSigningPage && {
        onClick: openSignatureDialog,
      })}
      fontSize={item?.fontSize || 11}
      className={item?.isActive ? 'z-100' : ''}
    >
      <div className="item-value-container">
        {item?.isActive && !isSigningPage && (
          <div ref={optionsRef} style={refStyle}>
            {!standardFieldsAndSignature.includes(item.itemId) &&
            !['file'].includes(item.type) ? (
              <OptionsBar
                item={item}
                currentSelectedItem={currentSelectedItem}
                addedFormFields={addedFormFields}
                updateFormFields={updateFormFields}
                removePlaceholder={() => handleRemovePlaceholder(item)}
              />
            ) : (
              <div className="options">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => handleRemovePlaceholder(item)}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.67046 0C3.35572 0 3.06314 0.136176 2.85425 0.36306C2.64665 0.588556 2.53638 0.886177 2.53638 1.18872V1.37743H1.76819H0.5C0.223858 1.37743 0 1.60129 0 1.87743C0 2.15358 0.223858 2.37743 0.5 2.37743H1.26819V8.07593C1.26819 8.37847 1.37846 8.67609 1.58607 8.90159C1.79495 9.12847 2.08753 9.26465 2.40228 9.26465H6.84088C7.15562 9.26465 7.4482 9.12847 7.65709 8.90159C7.8647 8.6761 7.97497 8.37848 7.97497 8.07593V2.37743H8.74312C9.01927 2.37743 9.24312 2.15358 9.24312 1.87743C9.24312 1.60129 9.01927 1.37743 8.74312 1.37743H7.47497H6.70681V1.18872C6.70681 0.886177 6.59654 0.588556 6.38893 0.36306C6.18005 0.136176 5.88746 0 5.57272 0H3.67046ZM3.03303 2.37743C3.03414 2.37744 3.03526 2.37744 3.03638 2.37744H6.20681C6.20793 2.37744 6.20904 2.37744 6.21016 2.37743H6.97497V8.07593C6.97497 8.1387 6.95163 8.19144 6.9214 8.22427C6.89246 8.2557 6.86248 8.26465 6.84088 8.26465H2.40228C2.38068 8.26465 2.35069 8.2557 2.32175 8.22427C2.29153 8.19144 2.26819 8.1387 2.26819 8.07593V2.37743H3.03303ZM5.70681 1.37743V1.18872C5.70681 1.12595 5.68347 1.07321 5.65325 1.04038C5.6243 1.00895 5.59432 1 5.57272 1H3.67046C3.64886 1 3.61888 1.00895 3.58994 1.04038C3.55972 1.07321 3.53638 1.12595 3.53638 1.18872V1.37743H5.70681Z"
                    fill="#676879"
                  />
                </svg>
              </div>
            )}
          </div>
        )}
        <div
          onClick={() => onItemClick(false)}
          style={{
            fontSize:
              (item?.fontStyle?.fontSize || item?.fontSize || 11) + 'px',
            lineHeight: item.itemId === 'checkbox' ? '' : 0.75,
            ...refStyle,
          }}
          ref={attachRef}
          {...(!isSigningPage && {
            className: `${isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${
              !standardFieldsAndSignature.includes(item.itemId) &&
              !['sd-textarea'].includes(item.itemId) &&
              !['file'].includes(item.type)
                ? item.isActive
                  ? 'active'
                  : 'hover-active'
                : ''
            }`,
          })}
        >
          {item.itemId ===
          APP_CONFID.form_fields.standard_fields.datePicker.id ? (
            wrapComponent(
              <DatePickerField
                item={item}
                setIsResizing={setIsResizing}
                editMode={true}
                isResizing={isResizing}
              />,
              'Date Picker',
            )
          ) : item.itemId === 'checkbox' ? (
            wrapComponent(
              <CheckboxEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              'Checkbox',
            )
          ) : item.itemId === 'sign-date' ? (
            wrapComponent(
              <SignDateEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              'Sign Date',
            )
          ) : item.itemId === 'text-box' ? (
            wrapComponent(
              <TextBoxEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              'Text Box',
            )
          ) : item.itemId === 'sd-textarea' ? (
            wrapComponent(
              <TextAreaEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              'Text Area',
            )
          ) : item.itemId === 'sd-dropdown' ? (
            wrapComponent(
              <DropdownEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              'Dropdown',
            )
          ) : item.itemId === 'line-item' ? (
            !isFeatureAvailable(pricingPlan, FEATURES.LINE_ITEMS) ? (
              <CustomTipseen
                content="This feature isn't available with your current plan. Please upgrade to use this and other exciting features."
                submitButtonText="Upgrade"
                theme="primary"
                submitButtonColor="#FFD383"
                submitButtonTextColor="#000"
                handleSubmit={() =>
                  window.open(
                    `https://${slug}.monday.com/apps/installed_apps/10050849`,
                  )
                }
              >
                {wrapComponent(
                  <LineItemEditOverlayChip
                    item={item}
                    setIsResizing={setIsResizing}
                  />,
                  'Sub Items',
                )}
              </CustomTipseen>
            ) : (
              wrapComponent(
                <LineItemEditOverlayChip
                  item={item}
                  setIsResizing={setIsResizing}
                />,
                'Sub Items',
              )
            )
          ) : SIGNATURE_FIELDS.includes(item.itemId) ? (
            wrapComponent(
              <SignatureAndInitialsEditChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              item?.title.includes('Signature')
                ? t('Signature')
                : t('Initials'),
            )
          ) : item.itemId === 'sd-image' ? (
            wrapComponent(
              <ImageEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              'Image',
            )
          ) : item.type === 'file' ? (
            wrapComponent(
              <PrefilledFieldsImageEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              item.title || 'File',
            )
          ) : item.itemId === 'sd-link' || item.type === 'link' ? (
            wrapComponent(
              <LinkEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
                mondayBoardValues={mondayBoardValues}
              />,
              item?.title || 'Link',
            )
          ) : item.itemId === 'sd-payment' && isV3 ? (
            wrapComponent(
              <CustomButtonWrapper
                // contentEditable
                // suppressContentEditableWarning
                // onBlur={handleBlur}
                // onKeyDown={handleKeyDown}
                buttonColor={item?.color}
              >
                {item?.text || t('Make Payment')}
              </CustomButtonWrapper>,
              'Payment Item',
            )
          ) : item.itemId === 'sd-payment' && !isV3 ? (
            wrapComponent(
              <CustomButtonWrapper
                contentEditable
                suppressContentEditableWarning
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                buttonColor={item?.color}
              >
                {item?.text || t('Make Payment')}
              </CustomButtonWrapper>,
              'Payment Item',
            )
          ) : (
            wrapComponent(
              <PrefilledFieldsEditOverlayChip
                item={item}
                setIsResizing={setIsResizing}
              />,
              item?.title || 'Chip',
            )
          )}
        </div>
      </div>
    </ChipContainer>
  );
};

export default DraggableChips;
